// SAMA + PDPL Compliance Rules Engine
// Pure evaluators returning structured verdicts so any action button can be gated.
//
// All rules below are simulated against the synthetic debtor data — they map to
// the spirit of SAMA's Debt Collection Regulations (≤10 attempts / 30d, contact
// window 09:00–21:00 KSA, opt-out, vulnerable-customer hold, dispute hold,
// language-match, recording required).

export const SAMA_RULES = {
  CALL_LIMIT_30D: 10,         // max phone-contact attempts per debtor / 30 days
  CONTACT_WINDOW: { start: 9, end: 21 }, // 09:00–21:00 KSA local
  CHANNEL_LIMIT_30D: 20,      // soft cap across any single channel
};

export const SEVERITY = {
  INFO: { tone: 'info',     label: 'Info',          weight: 0 },
  WARN: { tone: 'warning',  label: 'Warning',       weight: 1 },
  HIGH: { tone: 'destructive', label: 'High',       weight: 2 },
  CRITICAL: { tone: 'destructive', label: 'Critical', weight: 3 },
  REG: { tone: 'destructive', label: 'Regulatory',  weight: 4 },
};

// Each evaluator returns { allowed, reason, severity, rule }.
const PASS = { allowed: true, reason: 'OK', severity: SEVERITY.INFO, rule: 'none' };

const contactWindow = (debtor) => {
  // Demo-friendly: contact-window block is a per-debtor flag (e.g. the customer
  // requested calls only between 17:00–20:00 and we are outside that window).
  // We keep this per-case so a single debtor demonstrates the rule rather than
  // every debtor going dark whenever the demo runs after-hours.
  if (debtor?.contactWindow?.blocked) {
    return {
      allowed: false,
      reason: debtor.contactWindow.reason || 'Outside debtor-preferred contact window (17:00–20:00 KSA)',
      severity: SEVERITY.REG,
      rule: 'SAMA · Contact window',
    };
  }
  return PASS;
};

const attemptLimit = (debtor, channel) => {
  const attempts = debtor?.contactStats?.[channel + '30d'] ?? 0;
  const cap = channel === 'call' ? SAMA_RULES.CALL_LIMIT_30D : SAMA_RULES.CHANNEL_LIMIT_30D;
  if (attempts >= cap) {
    return {
      allowed: false,
      reason: `SAMA cap reached: ${attempts}/${cap} ${channel} attempts in last 30 days`,
      severity: SEVERITY.REG,
      rule: `SAMA · ${channel} attempt limit`,
    };
  }
  if (attempts >= cap - 2) {
    return {
      allowed: true,
      reason: `Near SAMA cap: ${attempts}/${cap} ${channel} attempts in last 30 days`,
      severity: SEVERITY.WARN,
      rule: `SAMA · ${channel} attempt limit`,
    };
  }
  return PASS;
};

const optOut = (debtor, channel) => {
  if (debtor?.consent && debtor.consent[channel] === false) {
    return {
      allowed: false,
      reason: `Debtor opted out of ${channel} — strict block`,
      severity: SEVERITY.HIGH,
      rule: 'PDPL · Opt-out',
    };
  }
  return PASS;
};

const holds = (debtor) => {
  if (debtor?.holds?.dispute) return {
    allowed: false, reason: 'Dispute hold active — outreach blocked until resolution',
    severity: SEVERITY.HIGH, rule: 'Policy · Dispute hold',
  };
  if (debtor?.holds?.hardship) return {
    allowed: false, reason: 'Hardship review in progress — contact paused',
    severity: SEVERITY.HIGH, rule: 'Policy · Hardship hold',
  };
  if (debtor?.holds?.legal) return {
    allowed: false, reason: 'Legal hold — contact via legal channel only',
    severity: SEVERITY.HIGH, rule: 'Policy · Legal hold',
  };
  return PASS;
};

const recordingNotice = () => ({
  allowed: true,
  reason: 'Call will be recorded and stored encrypted (SAMA · PDPL)',
  severity: SEVERITY.INFO,
  rule: 'SAMA · Recording disclosure',
});

// Evaluate a proposed action against the full ruleset.
//   action: 'call' | 'whatsapp' | 'sms' | 'email' | 'payment_link' | 'escalate_legal'
//   Returns the first BLOCKING verdict, or the worst non-blocking warning.
export const evaluateAction = (debtor, action) => {
  const channelMap = {
    call: 'call', whatsapp: 'whatsapp', sms: 'sms', email: 'email',
    payment_link: 'whatsapp', // delivered through messaging by default
    escalate_legal: null,
  };
  const channel = channelMap[action];

  // Universal holds first.
  const h = holds(debtor);
  if (!h.allowed) return h;

  if (channel) {
    const o = optOut(debtor, channel);
    if (!o.allowed) return o;
    const al = attemptLimit(debtor, channel);
    if (!al.allowed) return al;
    const cw = contactWindow(debtor);
    if (!cw.allowed) return cw;
    // Non-blocking — surface warning if attempts near cap.
    if (al.severity.weight > 0) return al;
    if (action === 'call') return recordingNotice();
  }

  return PASS;
};

// Convenience: evaluate all common actions at once (used by Resolution Options card).
export const evaluateAll = (debtor) => ({
  call:           evaluateAction(debtor, 'call'),
  whatsapp:       evaluateAction(debtor, 'whatsapp'),
  sms:            evaluateAction(debtor, 'sms'),
  email:          evaluateAction(debtor, 'email'),
  payment_link:   evaluateAction(debtor, 'payment_link'),
  escalate_legal: evaluateAction(debtor, 'escalate_legal'),
});
